import { useContext } from 'react';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { SegmentationDashboardWidget, Segments, WidgetInstance, WidgetPropsByType, WidgetSourceMetadata } from '@trinity/chat';
import { createNamedContext } from '@trinity/chat-components/utils';

// API Context Types for shared library
export type SaveWidgetRequest<T extends SegmentationDashboardWidget = SegmentationDashboardWidget> = {
  projectId: number;
  title: string;
  data: {
    type: T;
    props: WidgetPropsByType[T];
  };
  sourceMetadata?: WidgetSourceMetadata;
};

export type AddWidgetToDashboardRequest = {
  segmentId: number;
  blockId: string;
  /** Omit to place the widget directly in the block (only when the block allows it). */
  sectionId?: string;
  widgetId: number;
  /** 0-based row position; -1 appends at the end. */
  insertPosition: number;
};

export type PlacementSection = { id: string; title: string };

/**
 * A place "Add to Dashboard" can put a widget. Each adapter derives these from its own
 * storage — project segments from their page-builder config, hackathon segments from their
 * WidgetGrid layout — so the modal stays storage-agnostic.
 */
export type PlacementBlock = {
  id: string;
  title: string;
  sections: PlacementSection[];
  /** True when a widget can go straight into the block without choosing a section. */
  allowBlockLevel: boolean;
};

// Use existing types from the codebase but create a subset for dropdown usage
export type Segment = Pick<Segments.Segment, 'id' | 'name'>;

export type GetSegmentsResponse = {
  segments: Segment[];
};

// Context value interface
export interface ChartWidgetApiContextValue {
  // Save widget mutation
  useSaveWidget: () => UseMutationResult<WidgetInstance, Error, SaveWidgetRequest>;

  // Get segments query
  useGetSegments: (projectId: number, enabled?: boolean) => UseQueryResult<GetSegmentsResponse>;

  // Blocks/sections of one segment's dashboard a widget can be placed into
  useGetPlacementBlocks: (projectId: number, segmentId: number | null) => UseQueryResult<PlacementBlock[]>;

  // Place a saved widget into a segment's dashboard
  useAddWidgetToDashboard: (projectId: number) => UseMutationResult<unknown, Error, AddWidgetToDashboardRequest>;
}

// Create the context using the utility function
export const ChartWidgetApiContext = createNamedContext<ChartWidgetApiContextValue | undefined>(
  undefined,
  'ChartWidget.Api.Context',
);

// Hook to use the context
export const useChartWidgetApi = (): ChartWidgetApiContextValue => {
  const context = useContext(ChartWidgetApiContext);
  if (!context) {
    throw new Error('useChartWidgetApi must be used within a ChartWidgetApiProvider');
  }
  return context;
};
